import { Box, AppBar, Typography, IconButton, useMediaQuery, useTheme, Menu, MenuItem, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ReactComponent as Logo } from '../Assets/Images/logo.svg';
import { getProfile } from '../Api/ProfileApi';

const NavBar = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const [profileAnchorEl, setProfileAnchorEl] = useState(null);
    const [isFetchingProfile, setIsFetchingProfile] = useState(false);
    const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const [profile, setProfile] = useState(null);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const res = await getProfile();
                const profileData = res?.data || res;
                if (profileData) {
                    setProfile(profileData);
                }
            } catch (error) {
                console.error('Failed to load profile in NavBar:', error);
            }
        };
        fetchProfile();
    }, []);

    const menuItems = [
        { text: 'Dashboard', icon: <DashboardOutlinedIcon sx={{ fontSize: 20 }} />, path: '/dashboard' },
        { text: 'Post Job', icon: <AddIcon sx={{ fontSize: 20 }} />, path: '/create-new-job' },
        { text: 'Jobs', icon: <WorkOutlineOutlinedIcon sx={{ fontSize: 20 }} />, path: '/jobs' },
        { text: 'Search', icon: <PersonSearchOutlinedIcon sx={{ fontSize: 20 }} />, path: '/search-candidate' },
        { text: 'Candidates', icon: <PeopleOutlineOutlinedIcon sx={{ fontSize: 20 }} />, path: '/candidates' },
        { text: 'Interviews', icon: <CalendarMonthOutlinedIcon sx={{ fontSize: 20 }} />, path: '/interviews' },
    ];

    const handleMobileDrawerToggle = () => {
        setMobileDrawerOpen(!mobileDrawerOpen);
    };

    const handleNavigation = (path) => {
        navigate(path);
        setMobileDrawerOpen(false);
    };

    const handleProfileMenuOpen = (event) => {
        setProfileAnchorEl(event.currentTarget);
    };

    const handleProfileMenuClose = () => {
        setProfileAnchorEl(null);
    };

    const handleViewProfile = async () => {
        try {
            setIsFetchingProfile(true);
            const profileData = await getProfile();
            navigate('/profile-settings', { state: { profile: profileData } });
        } catch (error) {
            console.error('Failed to fetch profile details:', error);
        } finally {
            setIsFetchingProfile(false);
            handleProfileMenuClose();
        }
    };

    const handleLogout = () => {
        handleProfileMenuClose();
        setLogoutDialogOpen(true);
    };

    const handleConfirmLogout = () => {
        setLogoutDialogOpen(false);
        localStorage.removeItem('token');
        localStorage.removeItem('authToken');
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('user');
        localStorage.removeItem('hasCompany');
        navigate('/login', { replace: true });
    };

    return (
        <>
            <AppBar position="fixed" sx={{
                bgcolor: '#FFFFFF',
                boxShadow: 'none',
                borderBottom: '1px solid #E5E7EB',
                height: '60px',
                zIndex: theme.zIndex.drawer + 1,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: { xs: 2, sm: 3, md: 4 },
            }}>
                {/* Left: Logo + Hamburger (mobile) */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {isMobile && (
                        <IconButton onClick={handleMobileDrawerToggle} sx={{ color: '#374151', p: 0.5 }}>
                            <MenuIcon sx={{ width: 24, height: 24 }} />
                        </IconButton>
                    )}
                    <Box 
                        sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }} 
                        onClick={() => navigate('/dashboard')}
                    >
                        <Logo style={{ width: '80px', height: 'auto', objectFit: 'contain' }} />
                    </Box>
                </Box>

                {/* Center: Navigation Links (desktop only) */}
                {!isMobile && (
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 0.5,
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)',
                    }}>
                        {menuItems.map((item) => {
                            const isActive = location.pathname === item.path;
                            return (
                                <Box
                                    key={item.text}
                                    onClick={() => handleNavigation(item.path)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        px: 1.5,
                                        py: 1,
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        position: 'relative',
                                        transition: 'all 0.2s ease',
                                        color: isActive ? '#6B31C4' : '#6B7280',
                                        backgroundColor: isActive ? 'rgba(107, 49, 196, 0.06)' : 'transparent',
                                        '&:hover': {
                                            color: '#6B31C4',
                                            backgroundColor: 'rgba(107, 49, 196, 0.04)',
                                        },
                                    }}
                                >
                                    {item.icon}
                                    <Typography sx={{
                                        fontSize: '0.8125rem',
                                        fontWeight: isActive ? 600 : 500,
                                        letterSpacing: '0.01em',
                                        whiteSpace: 'nowrap',
                                    }}>
                                        {item.text}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                )}

                {/* Right: Profile */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box 
                        onClick={handleProfileMenuOpen} 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1, 
                            cursor: 'pointer',
                            px: 1.5,
                            py: 0.75,
                            borderRadius: '8px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: '#F3F4F6',
                            },
                        }}
                    >
                        <Box sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            backgroundColor: '#F3F4F6',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <PersonOutlineOutlinedIcon sx={{ color: '#6B7280', fontSize: 18 }} />
                        </Box>
                        {!isMobile && (
                            <Typography sx={{ 
                                color: '#374151', 
                                fontSize: '0.8125rem',
                                fontWeight: 500,
                                maxWidth: '160px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}>
                                {profile?.email || 'Account'}
                            </Typography>
                        )}
                    </Box>

                    {/* Profile Dropdown Menu */}
                    <Menu
                        anchorEl={profileAnchorEl}
                        open={Boolean(profileAnchorEl)}
                        onClose={handleProfileMenuClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        PaperProps={{
                            sx: {
                                mt: 1,
                                minWidth: 180,
                                border: '1px solid #E5E7EB',
                                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
                                borderRadius: '10px',
                            }
                        }}
                    >
                        <MenuItem onClick={handleViewProfile} disabled={isFetchingProfile} sx={{ gap: 1.5, py: 1.25, fontSize: '0.875rem' }}>
                            <VisibilityOutlinedIcon sx={{ fontSize: 18, color: '#6B7280' }} />
                            <Typography sx={{ fontSize: '0.875rem', color: '#374151' }}>View Profile</Typography>
                        </MenuItem>
                        <Divider sx={{ mx: 1 }} />
                        <MenuItem onClick={handleLogout} sx={{ gap: 1.5, py: 1.25, color: '#EF4444' }}>
                            <LogoutOutlinedIcon sx={{ fontSize: 18 }} />
                            <Typography sx={{ fontSize: '0.875rem' }}>Logout</Typography>
                        </MenuItem>
                    </Menu>
                </Box>
            </AppBar>

            {/* Mobile Drawer — clean slide-down style */}
            <Drawer 
                anchor="top" 
                open={mobileDrawerOpen} 
                onClose={handleMobileDrawerToggle}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': { 
                        width: '100%', 
                        background: '#FFFFFF', 
                        color: '#374151', 
                        pt: '60px',
                        borderBottom: '1px solid #E5E7EB',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
                    },
                }}
            >
                {/* Close Button */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, py: 1 }}>
                    <IconButton onClick={handleMobileDrawerToggle} sx={{ color: '#6B7280' }}>
                        <CloseIcon sx={{ fontSize: 22 }} />
                    </IconButton>
                </Box>

                {/* Menu Items */}
                <List sx={{ px: 2, pb: 2 }}>
                    {menuItems.map((item) => {
                        const isActive = location.pathname === item.path;
                        return (
                            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                                <ListItemButton
                                    onClick={() => handleNavigation(item.path)}
                                    sx={{
                                        borderRadius: '8px',
                                        py: 1.25,
                                        px: 2,
                                        color: isActive ? '#6B31C4' : '#374151',
                                        backgroundColor: isActive ? 'rgba(107, 49, 196, 0.06)' : 'transparent',
                                        '&:hover': {
                                            backgroundColor: isActive ? 'rgba(107, 49, 196, 0.08)' : '#F3F4F6',
                                        },
                                    }}
                                >
                                    <ListItemIcon sx={{ color: isActive ? '#6B31C4' : '#6B7280', minWidth: 36 }}>
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary={item.text} 
                                        primaryTypographyProps={{ 
                                            fontSize: '0.875rem', 
                                            fontWeight: isActive ? 600 : 400 
                                        }} 
                                    />
                                </ListItemButton>
                            </ListItem>
                        );
                    })}
                </List>
            </Drawer>

            {/* Logout Confirmation Dialog */}
            <Dialog
                open={logoutDialogOpen}
                onClose={() => setLogoutDialogOpen(false)}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        px: 1,
                        py: 0.5,
                        maxWidth: 400
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Confirm Logout</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: 'text.secondary', fontSize: '0.925rem' }}>
                        Are you sure you want to log out of RonoJobs? You will need to enter your credentials to log in again.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button 
                        onClick={() => setLogoutDialogOpen(false)} 
                        variant="outlined"
                        sx={{ 
                            borderRadius: 2, 
                            textTransform: 'none', 
                            borderColor: '#D1D5DB',
                            color: '#374151',
                            '&:hover': {
                                bgcolor: '#F9FAFB',
                                borderColor: '#9CA3AF'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleConfirmLogout} 
                        variant="contained"
                        color="error"
                        sx={{ 
                            borderRadius: 2, 
                            textTransform: 'none',
                            bgcolor: '#EF4444',
                            '&:hover': {
                                bgcolor: '#DC2626'
                            }
                        }}
                    >
                        Logout
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default NavBar